import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ExternalLink, Pencil, X } from 'lucide-react';

export interface SiteContentDetail {
  id: string;
  content_id: string;
  title: string | null;
  summary: string | null;
  url: string | null;
  pub_date: string | null;
  scheduled_for: string | null;
  status: string | null;
  arc_slug: string | null;
  arc_stage: string | null;
  source_thought_id: number | null;
  /** Display label for the arc (optional). */
  arc_label?: string | null;
}

export type SiteContentDetailSave = {
  title: string;
  summary: string | null;
  scheduled_for: string | null;
  arc_slug: string;
  source_thought_id: number;
  arc_stage: string | null;
};

export type SiteContentArcOption = {
  slug: string;
  label: string;
};

export type SiteContentIdeaOption = {
  id: number;
  title: string | null;
  arc_slug: string | null;
  arc_stage: string | null;
};

interface SiteContentDetailModalProps {
  isOpen: boolean;
  detail: SiteContentDetail | null;
  arcs: SiteContentArcOption[];
  ideas: SiteContentIdeaOption[];
  onClose: () => void;
  onSave: (id: string, patch: SiteContentDetailSave) => Promise<void>;
  onOpenThought?: (thoughtId: string) => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

function scheduleIso(detail: SiteContentDetail): string {
  return (detail.scheduled_for ?? detail.pub_date ?? '').slice(0, 10);
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-mono text-gray-400 dark:text-gray-500 mb-1">
        {label}
      </div>
      <div className="text-[15px] text-gray-900 dark:text-gray-100 leading-snug">{children}</div>
    </div>
  );
}

function EditField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-mono text-gray-400 dark:text-gray-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  'w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';

export default function SiteContentDetailModal({
  isOpen,
  detail,
  arcs,
  ideas,
  onClose,
  onSave,
  onOpenThought,
}: SiteContentDetailModalProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [arcSlug, setArcSlug] = useState('');
  const [thoughtId, setThoughtId] = useState('');

  useEffect(() => {
    if (!isOpen || !detail) {
      setEditing(false);
      return;
    }
    setEditing(false);
    setTitle(detail.title?.trim() || '');
    setSummary(detail.summary?.trim() || '');
    setScheduledFor(scheduleIso(detail));
    setArcSlug(detail.arc_slug ?? '');
    setThoughtId(detail.source_thought_id != null ? String(detail.source_thought_id) : '');
  }, [isOpen, detail]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (editing && !saving) {
        setEditing(false);
        return;
      }
      if (!saving) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, editing, saving]);

  const ideasForArc = useMemo(
    () => ideas.filter((t) => t.arc_slug === arcSlug),
    [ideas, arcSlug],
  );

  const ideaLabel = useMemo(() => {
    if (detail?.source_thought_id == null) return '—';
    const idea = ideas.find((t) => t.id === detail.source_thought_id);
    return idea?.title?.trim() || `Idea #${detail.source_thought_id}`;
  }, [detail, ideas]);

  if (!isOpen || !detail) return null;

  const displayTitle = detail.title?.trim() || detail.content_id;
  const isDraft = (detail.status ?? '').toLowerCase() === 'draft';
  const stage = detail.arc_stage
    ? detail.arc_stage.charAt(0).toUpperCase() + detail.arc_stage.slice(1)
    : '—';
  const arcDisplay =
    detail.arc_label?.trim() ||
    arcs.find((a) => a.slug === detail.arc_slug)?.label ||
    detail.arc_slug ||
    '—';

  function startEdit() {
    if (!detail) return;
    setTitle(detail.title?.trim() || '');
    setSummary(detail.summary?.trim() || '');
    setScheduledFor(scheduleIso(detail));
    setArcSlug(detail.arc_slug ?? '');
    setThoughtId(detail.source_thought_id != null ? String(detail.source_thought_id) : '');
    setEditing(true);
  }

  function cancelEdit() {
    if (!detail || saving) return;
    setTitle(detail.title?.trim() || '');
    setSummary(detail.summary?.trim() || '');
    setScheduledFor(scheduleIso(detail));
    setArcSlug(detail.arc_slug ?? '');
    setThoughtId(detail.source_thought_id != null ? String(detail.source_thought_id) : '');
    setEditing(false);
  }

  async function handleSave() {
    if (!detail || saving) return;
    const nextTitle = title.trim();
    if (!nextTitle) return;
    if (!arcSlug) return;
    if (!thoughtId) return;
    const nextSchedule = isDraft ? scheduledFor : scheduleIso(detail);
    if (isDraft && !/^\d{4}-\d{2}-\d{2}$/.test(nextSchedule)) return;

    const idea = ideas.find((t) => String(t.id) === thoughtId);
    if (!idea || idea.arc_slug !== arcSlug) return;

    setSaving(true);
    try {
      await onSave(detail.id, {
        title: nextTitle,
        summary: summary.trim() || null,
        scheduled_for: nextSchedule || null,
        arc_slug: arcSlug,
        source_thought_id: idea.id,
        arc_stage: idea.arc_stage,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-center z-50 p-0 sm:p-4"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-content-detail-title"
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-mono text-gray-400 dark:text-gray-500 mb-1">
              Site content{editing ? ' · editing' : ''}
            </div>
            <h2
              id="site-content-detail-title"
              className="text-xl font-semibold text-gray-900 dark:text-white leading-snug"
            >
              {editing ? title.trim() || 'Untitled' : displayTitle}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!saving) onClose();
            }}
            className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {editing ? (
            <>
              <EditField label="Name">
                <input
                  type="text"
                  autoFocus
                  className={inputClass}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Short title"
                />
              </EditField>
              <EditField label="Description">
                <textarea
                  className={`${inputClass} min-h-[72px] resize-none`}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="One or two sentences"
                />
              </EditField>
              <EditField label="Schedule date">
                <input
                  type="date"
                  className={inputClass}
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  disabled={!isDraft}
                />
                {!isDraft && (
                  <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                    Published date is locked.
                  </p>
                )}
              </EditField>
              <EditField label="Arc group">
                <select
                  className={inputClass}
                  value={arcSlug}
                  onChange={(e) => {
                    const next = e.target.value;
                    setArcSlug(next);
                    const stillValid = ideas.some(
                      (t) => t.arc_slug === next && String(t.id) === thoughtId,
                    );
                    if (!stillValid) {
                      const first = ideas.find((t) => t.arc_slug === next);
                      setThoughtId(first ? String(first.id) : '');
                    }
                  }}
                >
                  <option value="">Select arc…</option>
                  {arcs.map((a) => (
                    <option key={a.slug} value={a.slug}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </EditField>
              <EditField label="Tied idea">
                <select
                  className={inputClass}
                  value={thoughtId}
                  onChange={(e) => setThoughtId(e.target.value)}
                  disabled={!arcSlug}
                >
                  <option value="">Select idea…</option>
                  {ideasForArc.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.title?.trim() || `Idea #${t.id}`}
                    </option>
                  ))}
                </select>
                {arcSlug && ideasForArc.length === 0 && (
                  <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                    No ideas on this arc yet.
                  </p>
                )}
              </EditField>
            </>
          ) : (
            <>
              <Field label="Description">
                {detail.summary?.trim() || (
                  <span className="text-gray-400 dark:text-gray-500">No description</span>
                )}
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Schedule date">{formatDate(scheduleIso(detail) || null)}</Field>
                <Field label="Status">{detail.status ?? '—'}</Field>
                <Field label="Arc group">{arcDisplay}</Field>
                <Field label="Arc stage">{stage}</Field>
              </div>

              <Field label="Tied idea">
                {detail.source_thought_id != null && onOpenThought ? (
                  <button
                    type="button"
                    onClick={() => onOpenThought(String(detail.source_thought_id))}
                    className="text-blue-700 dark:text-blue-300 hover:underline text-left"
                  >
                    {ideaLabel}
                  </button>
                ) : (
                  ideaLabel
                )}
              </Field>

              {detail.url ? (
                <Field label="URL">
                  <a
                    href={detail.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-blue-700 dark:text-blue-300 hover:underline break-all"
                  >
                    {detail.url}
                    <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  </a>
                </Field>
              ) : null}

              <Field label="Content id">
                <span className="font-mono text-[13px] text-gray-600 dark:text-gray-300">
                  {detail.content_id}
                </span>
              </Field>
            </>
          )}
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-3 flex flex-wrap justify-end gap-2">
          {editing ? (
            <>
              <button
                type="button"
                disabled={saving}
                onClick={cancelEdit}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  saving ||
                  !title.trim() ||
                  !arcSlug ||
                  !thoughtId ||
                  (isDraft && !/^\d{4}-\d{2}-\d{2}$/.test(scheduledFor))
                }
                onClick={() => void handleSave()}
                className="px-3 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={startEdit}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 mr-auto"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
              {detail.url ? (
                <a
                  href={detail.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Open on site
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
